import { tracklistSchema } from "@api/tracklists/schemas";
import { filterTracks } from "@api/tracklists/utils";
import { fetchApiResource, fetchResource } from "@app/utils/";
import { z } from "zod";
import { artworkSchema, oldUserSchema, userBioSchema, userCategorySchema, userSchema, userTagsSchema } from "./schemas";
import type { User, UserTags } from "./types";

/**
 * fetch specific user
 */
export const getUser = (uid: string) =>
	fetchResource(`user/${uid}`, userSchema, "GET", undefined, { apiVersion: "v2" });

/**
 * fetch user's followed users
 */
export const getMyVoxers = (page = 1) => fetchResource(`user/follows/me?page=${page}`, z.array(oldUserSchema));

/**
 * follow specific user
 */
export const getUserFollowers = (page: number, uid: string) =>
	fetchResource(`user/follows/${uid}?page=${page}`, z.array(oldUserSchema));

/**
 * follow specific user
 */
export const postFollowUser = (uid: string) =>
	fetchResource(`user/follows/${uid}`, z.object({}), "POST", undefined, { emptyResponse: true });
/**
 * unfollow specific user
 */
export const deleteFollowUser = (uid: string) =>
	fetchResource(`user/follows/${uid}`, z.object({}), "DELETE", undefined, { emptyResponse: true });
/**
 * fetch list of currently trending users
 */
export const getTrendingUsers = (page = 1) => fetchResource(`trendings/users?page=${page}`, z.array(oldUserSchema));

/**
 * fetch url to user's signature
 */
export const getSignature = (uid: string) => fetchResource(`user/${uid}/signature`, z.object({ url: z.string() }));
/**
 * delete user's signature
 */
export const deleteSignature = () =>
	fetchResource("user/signature", z.object({}), "DELETE", null, { emptyResponse: true });
/**
 * fetch predefined user background artworks
 */
export const getUserArtworks = () => fetchResource("artwork/user-bg", z.array(artworkSchema));

/**
 * patch user
 */
export const patchUser = (data: FormData) => fetchResource("user", oldUserSchema, "PATCH", data, { formData: true });
/**
 * Request deletion of profile
 */
export const postDeleteUser = () =>
	fetchResource(
		"user/delete",
		z.object({}),
		"POST",
		{ url: `${window.location.origin}/auth/delete/%TOKEN%` },
		{ emptyResponse: true },
	);

/**
 * Delete user
 * @param token token from URL
 */
export const deleteUser = (token: string) => {
	return fetchApiResource(`user/${token}`, "DELETE");
};
/**
 * Get user's saved preferences
 */
export const getPreferences = () =>
	fetchResource("user/preferences", z.array(z.object({ id: z.number(), name: z.string() })));

export const getUsersTags = (uid: string) => fetchResource(`user/${uid}/tags`, userTagsSchema);

export const getUserBio = (uid: string) => fetchResource(`user/${uid}/bio`, userBioSchema);

export const putUserBio = (data: FormData) =>
	fetchResource("user/bio", z.object({}), "PUT", data, { formData: true, emptyResponse: true });

export const deleteUserBio = () =>
	fetchResource("user/bio", z.object({}), "DELETE", undefined, { emptyResponse: true });

export const getUserCategories = () => fetchResource("user/category", z.array(userCategorySchema));

export const patchUserTags = (tags: Partial<{ category: number | null } & Omit<UserTags, "category">>) =>
	fetchResource("user/tags", z.object({}), "PATCH", tags, { emptyResponse: true });
/**
 * Get published tracklists created by a specific user
 */
export const getProfileFeed = async (uid: User["uid"], page = 1) => {
	const res = await fetchResource(`user/${uid}/tracklists?page=${page}`, z.array(tracklistSchema), "GET", undefined, {
		apiVersion: "v2",
	});
	return res.map((tr) => ({ ...tr, tracks: filterTracks(tr.tracks) }));
};

/**
 * Block a specific user
 */
export const postBlockUser = (uid: User["uid"]) =>
	fetchResource(`user/blocks/${uid}`, z.object({}), "POST", undefined, { emptyResponse: true });

/**
 * Unblock specific user
 */
export const deleteBlockUser = (uid: User["uid"]) =>
	fetchResource(`user/blocks/${uid}`, z.object({}), "DELETE", undefined, { emptyResponse: true });

/**
 * Get user's 20 latests tracklists with location
 */
export const getLatestUserTracklists = (uid: User["uid"]) =>
	fetchResource(`user/${uid}/tracklists/location/latest`, z.array(tracklistSchema));
